import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateRoutineInput,
  UpdateRoutineInput,
} from "@shared/validation/routine.schema";
import { apiClient, ApiError } from "./client";

export type RoutineDaypart = "morning" | "midday" | "afternoon" | "evening" | "bedtime";
export type RoutineCategory =
  | "nap"
  | "meal"
  | "activity"
  | "medication"
  | "bedtime"
  | "other";

export interface RoutineSummary {
  id: string;
  childId: string;
  label: string;
  daypart: RoutineDaypart;
  category: RoutineCategory;
  timeText: string | null;
  details: string | null;
  sortOrder: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

const routinesKey = (childId: string) => ["children", childId, "routines"] as const;

export function useRoutines(childId: string | undefined) {
  return useQuery<RoutineSummary[], ApiError>({
    queryKey: childId ? routinesKey(childId) : ["children", "__none__", "routines"],
    queryFn: () => apiClient<RoutineSummary[]>(`/api/children/${childId}/routines`),
    enabled: Boolean(childId),
  });
}

export function useCreateRoutine(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<RoutineSummary, ApiError, CreateRoutineInput>({
    mutationFn: (vars) =>
      apiClient<RoutineSummary>(`/api/children/${childId}/routines`, {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: routinesKey(childId) });
    },
  });
}

export function useUpdateRoutine(childId: string, routineId: string) {
  const queryClient = useQueryClient();
  return useMutation<RoutineSummary, ApiError, UpdateRoutineInput>({
    mutationFn: (vars) =>
      apiClient<RoutineSummary>(`/api/children/${childId}/routines/${routineId}`, {
        method: "PUT",
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: routinesKey(childId) });
    },
  });
}

export function useDeleteRoutine(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ deleted: true }, ApiError, string>({
    mutationFn: (routineId) =>
      apiClient<{ deleted: true }>(`/api/children/${childId}/routines/${routineId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: routinesKey(childId) });
    },
  });
}
